import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, useToast } from '../../components/ui';
import { getOutreachOverview, getOutreachSettings } from '../../utils/api';
import RunResearchModal from '../components/RunResearchModal';
import { ACTIVE_RUN_STATUSES, ACTIVITY_LABELS, MODE_LABELS, PRIORITY_PILL, RUN_STATUS_LABELS, STATUS_LABELS, STATUS_PILL, fmtDateTime, fmtDuration, fmtPct, loadError, runProgress } from '../utils';

const POLL_MS = 10000;

export default function OverviewPage() {
  const navigate = useNavigate();
  const [showToast, toastNode] = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [showRun, setShowRun] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => getOutreachOverview().then(d => { setData(d); setError(''); }).catch(e => setError(loadError(e, 'Could not load the outreach overview'))), []);

  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { getOutreachSettings().then(r => setSettings(r.settings)).catch(() => {}); }, []);

  const active = data?.lastRun && ACTIVE_RUN_STATUSES.includes(data.lastRun.status);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => load(), POLL_MS);
    return () => clearInterval(id);
  }, [active, load]);

  const k = data?.kpis || {};
  const lastRun = data?.lastRun;
  const progress = active ? runProgress(lastRun) : null;

  return (
    <>
      <PageHeader
        title="Provider Outreach"
        subtitle="Medicaid-serving primary care offices across the agency's service areas in Wayne, Oakland and Macomb County, researched from public sources and tracked by the team."
        meta={lastRun ? `Last run ${fmtDateTime(lastRun.finishedAt || lastRun.startedAt || lastRun.createdAt)}` : 'No research run yet'}
        actions={(
          <>
            <button type="button" className="ops-btn ops-btn--ghost ops-btn--sm" onClick={() => setTick(t => t + 1)}><Icon name="refresh" size={14} /> Refresh</button>
            <Link to="/outreach/offices?add=1" className="ops-btn ops-btn--sm"><Icon name="plus" size={14} /> Add office</Link>
            <button type="button" className="ops-btn ops-btn--primary ops-btn--sm" onClick={() => setShowRun(true)} disabled={Boolean(active)}><Icon name="refresh" size={14} /> {active ? 'Run in progress' : 'Run research'}</button>
          </>
        )}
      />

      {error && <div className="ops-error">{error}</div>}

      <div className="ops-kpis">
        <StatCard label="Total offices" value={data ? k.totalOffices ?? 0 : '…'} sub={k.needsReview ? `${k.needsReview} need review` : 'Active records'} to="/outreach/offices" />
        <StatCard label="New opportunities" value={data ? k.newOpportunities ?? 0 : '…'} sub="Found by the last run" tone="blue" to="/outreach/offices?changeType=NEW" />
        <StatCard label="High priority" value={data ? k.highPriority ?? 0 : '…'} sub="P1 offices" tone="red" to="/outreach/offices?priority=P1" />
        <StatCard label="Contacted" value={data ? k.contacted ?? 0 : '…'} sub="Any team contact" to="/outreach/tracker" />
        <StatCard label="Follow-ups due" value={data ? k.followUpsDue ?? 0 : '…'} sub="Due today or overdue" tone="amber" to="/outreach/tracker?bucket=due" />
        <StatCard label="Engaged practices" value={data ? k.engaged ?? 0 : '…'} sub="Engaged or referring" tone="green" to="/outreach/tracker?status=engaged" />
      </div>

      <div className="ops-split" style={{ marginTop: 18 }}>
        <Panel title="Priority outreach queue" count={data?.topOpportunities?.length} action={<Link className="ops-link" to="/outreach/offices?sort=priority">Open directory</Link>} flush>
          {!data ? <div style={{ padding: 18 }}><Skeleton rows={5} /></div> : !data.topOpportunities?.length ? <Empty title="No offices yet. Run research or import the outreach workbook to get started." /> : (
            <div className="ops-table-wrap">
              <table className="ops-table ops-table--dense">
                <thead><tr><th>Office / practice</th><th>Area</th><th>Priority</th><th>Medicaid signal</th><th>Outreach status</th></tr></thead>
                <tbody>
                  {data.topOpportunities.map(o => (
                    <tr key={o.officeId} className="is-clickable" onClick={() => navigate(`/outreach/offices?office=${encodeURIComponent(o.officeId)}`)}>
                      <td><div className="ops-cell-main">{o.name}</div><div className="ops-cell-sub">{o.practiceType || 'Practice type unknown'}{o.phoneDisplay ? ` · ${o.phoneDisplay}` : ''}</div></td>
                      <td>{o.areaLabel || o.city || ''}</td>
                      <td><StatusPill status={PRIORITY_PILL[o.priority] || 'neutral'} label={o.priority || 'REVIEW'} /></td>
                      <td>{(o.analysis || o.importedAnalysis)?.medicaidSignal?.level ? <span className={o.analysis.medicaidSignal.level === 'confirmed' ? 'ops-readout' : ''}>{o.analysis.medicaidSignal.level}</span> : <span className="muted">Not analyzed</span>}</td>
                      <td><StatusPill status={STATUS_PILL[o.outreachStatus] || 'neutral'} label={STATUS_LABELS[o.outreachStatus] || 'Not contacted'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <div style={{ display: 'grid', gap: 18, alignContent: 'start' }}>
          <Panel title="Research activity" action={<Link className="ops-link" to="/outreach/runs">All runs</Link>}>
            {!data ? <Skeleton rows={3} /> : !lastRun ? <div className="ops-help">No research run has been started. Use Run research to collect public source data for the configured areas.</div> : (
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div><div className="ops-cell-main">{MODE_LABELS[lastRun.mode] || lastRun.mode}</div><div className="ops-cell-sub">{fmtDateTime(lastRun.startedAt || lastRun.createdAt)}{lastRun.durationMs != null ? ` · ${fmtDuration(lastRun.durationMs)}` : ''}</div></div>
                  <StatusPill status={lastRun.status} label={RUN_STATUS_LABELS[lastRun.status] || lastRun.status} />
                </div>
                {active && <div className="ops-progress" role="progressbar" aria-valuenow={progress.percent} aria-valuemin={0} aria-valuemax={100} aria-label="Run progress"><div className="ops-progress__bar" style={{ width: `${progress.percent}%` }} /></div>}
                {active && <div className="ops-help">{progress.label} ({progress.percent}%). This page refreshes every 10 seconds.</div>}
                {lastRun.summary?.headline && <div className="ops-note">{lastRun.summary.headline}</div>}
                {lastRun.aiStats?.message && <div className="ops-info">{lastRun.aiStats.message}</div>}
                {!active && lastRun.delta && (
                  <div className="ops-stat-row">
                    <div className="ops-stat"><b>{lastRun.delta.created ?? 0}</b><span>New</span></div>
                    <div className="ops-stat"><b>{lastRun.delta.updated ?? 0}</b><span>Updated</span></div>
                    <div className="ops-stat"><b>{lastRun.delta.unchanged ?? 0}</b><span>Unchanged</span></div>
                    <div className="ops-stat"><b>{lastRun.aiStats?.modelCalls ?? 0}</b><span>AI calls</span></div>
                    <div className="ops-stat"><b>{lastRun.aiStats?.cacheHits ?? 0}</b><span>Cache hits</span></div>
                  </div>
                )}
                {!!lastRun.errors?.length && <div className="ops-error">{lastRun.errors.length} source error{lastRun.errors.length === 1 ? '' : 's'}. See the run detail.</div>}
                <Link className="ops-link" to={`/outreach/runs?run=${encodeURIComponent(lastRun.runId)}`}>Open run detail</Link>
              </div>
            )}
          </Panel>

          <Panel title="Current office coverage by area" action={<Link className="ops-link" to="/outreach/areas">Area intelligence</Link>} flush>
            {!data ? <div style={{ padding: 18 }}><Skeleton rows={4} /></div> : !data.areas?.length ? <Empty title="No areas configured" /> : (
              <ul className="ops-list">
                {data.areas.map(a => (
                  <li key={a.areaKey}>
                    <div className="ops-list__main"><strong>{a.label}{a.primary ? ' · primary' : ''}</strong><span>{a.counts?.offices ?? 0} offices · {a.counts?.byPriority?.P1 ?? 0} high priority{a.medicaidShare != null ? ` · Medicaid ${fmtPct(a.medicaidShare)}` : ''}</span></div>
                    <Link className="ops-link" to={`/outreach/offices?area=${encodeURIComponent(a.areaKey)}`}>View</Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <Panel title="Team outreach record" count={data?.recentActivity?.length} action={<Link className="ops-link" to="/outreach/tracker">Tracker</Link>} flush>
          {!data ? <div style={{ padding: 18 }}><Skeleton rows={4} /></div> : !data.recentActivity?.length ? <Empty title="No outreach activity recorded yet" /> : (
            <ul className="ops-list">
              {data.recentActivity.map(a => (
                <li key={a.activityId}>
                  <div className="ops-list__main">
                    <strong>{a.officeName || a.officeId}: {ACTIVITY_LABELS[a.type] || a.type}</strong>
                    <span>{a.summary}</span>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <StatusPill status={a.actorType === 'agent' ? 'running' : 'contacted'} label={a.actorType === 'agent' ? 'Agent' : (a.actor || 'Staff')} />
                    <div className="ops-cell-sub">{fmtDateTime(a.createdAt)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {showRun && (
        <RunResearchModal
          onClose={() => setShowRun(false)}
          emailDefault={settings?.notifications?.emailOnManualRun}
          costCapUsd={settings?.ai?.maxCostPerRunUsd}
          onStarted={(run) => { setShowRun(false); showToast(`Research run started (${MODE_LABELS[run.mode] || run.mode})`); setTick(t => t + 1); navigate(`/outreach/runs?run=${encodeURIComponent(run.runId)}`); }}
        />
      )}
      {toastNode}
    </>
  );
}
